import { createStore } from './config';
import {
  EIDPSource,
  type IIDPField,
  type IIDPMissingField,
  type IIDPFactInputData,
  type TRelatedOutputsContext,
} from '@/modules/idp/types';
import type { TResolvedValue } from '@/modules/outputs/types';

/**
 * State for the IDP inline panel that replaces the full-screen layover.
 * Controls visibility, data, and save callbacks for the panel shown
 * alongside Quill chat (automation page) or run cards (run page).
 */
interface IIDPPanelState {
  /** Whether the inline panel is currently open */
  isOpen: boolean;
  /** Source context: output or exception */
  source: EIDPSource;
  /** IDP data with factGroups (for exception mode) */
  idpData: IIDPFactInputData | null;
  /** Array of extracted IDP fields (for output mode) */
  fields: IIDPField[];
  /** Name of the document being analyzed */
  documentName: string;
  /** Whether the panel is in read-only mode */
  readOnly: boolean;
  /** Context type from related_outputs */
  context?: TRelatedOutputsContext;
  /** Exception fields for exception mode */
  exceptionFields: IIDPMissingField[];
  /** Callback to save edited values (for exception mode) */
  onSave?: (editedValues: Record<string, string>) => Promise<boolean>;
  /** Array of IDP output items for list mode */
  idpListItems?: Record<string, TResolvedValue>[];

  /** Opens the IDP inline panel with the given configuration */
  openPanel: (config: IIDPPanelOpenConfig) => void;
  /** Closes the IDP inline panel and resets state */
  closePanel: () => void;
}

/**
 * Configuration for opening the IDP inline panel.
 */
interface IIDPPanelOpenConfig {
  /** Source context: output or exception */
  source: EIDPSource;
  /** IDP data with factGroups (for exception mode) */
  idpData?: IIDPFactInputData | null;
  /** Array of extracted IDP fields (for output mode) */
  fields?: IIDPField[];
  /** Name of the document being analyzed */
  documentName: string;
  /** Whether the panel is in read-only mode */
  readOnly?: boolean;
  /** Context type from related_outputs */
  context?: TRelatedOutputsContext;
  /** Exception fields for exception mode */
  exceptionFields?: IIDPMissingField[];
  /** Callback to save edited values */
  onSave?: (editedValues: Record<string, string>) => Promise<boolean>;
  /** Array of IDP output items for list mode */
  idpListItems?: Record<string, TResolvedValue>[];
}

const initialState: Omit<IIDPPanelState, 'openPanel' | 'closePanel'> = {
  isOpen: false,
  source: EIDPSource.OUTPUT,
  idpData: null,
  fields: [],
  documentName: '',
  readOnly: false,
  context: undefined,
  exceptionFields: [],
  onSave: undefined,
  idpListItems: undefined,
};

export const useIDPPanelStore = createStore<IIDPPanelState>(
  'idp-panel-store',
  (_, _get, api) => ({
    ...initialState,

    openPanel: (config: IIDPPanelOpenConfig) => {
      api.setState(
        {
          isOpen: true,
          source: config.source,
          idpData: config.idpData ?? null,
          fields: config.fields ?? [],
          documentName: config.documentName,
          readOnly: config.readOnly ?? false,
          context: config.context,
          exceptionFields: config.exceptionFields ?? [],
          onSave: config.onSave,
          idpListItems: config.idpListItems,
        },
        false,
        { type: 'idpPanel/openPanel', config },
      );
    },

    closePanel: () => {
      api.setState({ ...initialState }, false, 'idpPanel/closePanel');
    },
  }),
);

/** Whether the IDP inline panel is currently open */
export const selectIDPPanelIsOpen = (state: IIDPPanelState) => state.isOpen;
/** Source context of the IDP panel */
export const selectIDPPanelSource = (state: IIDPPanelState) => state.source;
/** IDP data for exception mode */
export const selectIDPPanelIdpData = (state: IIDPPanelState) => state.idpData;
/** Fields for output mode */
export const selectIDPPanelFields = (state: IIDPPanelState) => state.fields;
/** Document name being analyzed */
export const selectIDPPanelDocumentName = (state: IIDPPanelState) =>
  state.documentName;
/** Whether the panel is read-only */
export const selectIDPPanelReadOnly = (state: IIDPPanelState) => state.readOnly;
/** Context type from related_outputs */
export const selectIDPPanelContext = (state: IIDPPanelState) => state.context;
/** Exception fields for exception mode */
export const selectIDPPanelExceptionFields = (state: IIDPPanelState) =>
  state.exceptionFields;
/** Save callback for exception mode */
export const selectIDPPanelOnSave = (state: IIDPPanelState) => state.onSave;
/** IDP list items for list mode */
export const selectIDPPanelListItems = (state: IIDPPanelState) =>
  state.idpListItems;
/** Open panel action */
export const selectIDPPanelOpen = (state: IIDPPanelState) => state.openPanel;
/** Close panel action */
export const selectIDPPanelClose = (state: IIDPPanelState) => state.closePanel;

export type { IIDPPanelState, IIDPPanelOpenConfig };
